"use client"

import { usePathname, useRouter, useSearchParams } from "next/navigation"
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarProvider,
  SidebarMenu,
} from "@/components/ui/custom-sidebar"
import { useSidebarMenu } from "@/components/general/SidebarMenuProvider"
import {
  ChevronLeft,
  ChevronRight,
  Dot
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useEffect } from "react";

type MainNavItem = {
  label: string
  href: string
}

type PageNavItem = {
  label: string
  panelId: string
  page: string
}

const mainNav: MainNavItem[] = [
  { label: "Home", href: "/" },
  { label: "Features", href: "/features" },
  { label: "Pricing", href: "/pricing" },
  { label: "Find", href: "/find" },
  // { label: "Search", href: "/search" },
  { label: "Ask", href: "/ask" },
  // { label: "Argue", href: "/argue" },
  { label: "Explore", href: "/explore" },
  // { label: "Bookmarks", href: "/bookmarks" },
  // { label: "Recent", href: "/recent" },
  // { label: "Data", href: "/data" },
  // { label: "Tools", href: "/tools" },
]

const pageNav: PageNavItem[] = [

  { label: "Searches", panelId: "searches", page: "/bookmarks" },
  { label: "Documents", panelId: "documents", page: "/bookmarks" },
  { label: "Paragraphs", panelId: "paragraphs", page: "/bookmarks" },

  // { label: "Document", panelId: "document", page: "/find" },
  // { label: "Case", panelId: "case", page: "/find" },
  // { label: "Facts", panelId: "facts", page: "/find" },

  // { label: "New", panelId: "new", page: "/ask" },
  // { label: "Answer", panelId: "answer", page: "/ask" },
  // { label: "Recommendations", panelId: "recommendations", page: "/ask" },
  // { label: "Canvas", panelId: "canvas", page: "/ask" },

  { label: "Searches", panelId: "searches", page: "/recent" },
  { label: "Documents", panelId: "documents", page: "/recent" },

  { label: "Text", panelId: "text", page: "/read" },
  { label: "Info", panelId: "info", page: "/read" },
  { label: "Facts", panelId: "facts", page: "/read" },
  { label: "Timeline", panelId: "timeline", page: "/read" },
  { label: "Reasoning", panelId: "reasoning", page: "/read" },
  { label: "Commentary", panelId: "commentary", page: "/read" },
  { label: "Citations", panelId: "citations", page: "/read" },
]

export function AppSidebar() {
  const pathname = usePathname()
  const router = useRouter()

  const { mode, setMode } = useSidebarMenu()

  // Secondary-law acts and treaties render in LawViewer, which has no
  // Facts/Reasoning panels (those are judgment-only).
  const readDocId = pathname.match(/^\/read\/([^/?]+)/)?.[1] ?? ""
  const isLawDoc = /^DOC_(TR|REG|DIR|DEC)_/.test(readDocId)
  const hiddenForLaw = new Set(["facts", "reasoning"])

  const pageMenu = pageNav
    .filter(p => pathname.includes(p.page))
    .filter(p => !(isLawDoc && hiddenForLaw.has(p.panelId)))
  const hasPanels = pageMenu.length > 0

  const searchParams = useSearchParams()
  const activePanel = searchParams.get("panel")

  useEffect(() => {
    if (activePanel) {
      setMode("panel")
    } else {
      setMode("main")
    }
  }, [pathname, activePanel]);

  return (
    <Sidebar
      side="left"
      collapsible="offcanvas"
      variant="sidebar"
      className="pt-16"
    >
      <SidebarContent className={cn(
        "pt-32"
      )}>
        <SidebarGroup className="pt-0">
          <SidebarMenu
            onMouseLeave={() => {
              if (hasPanels) setMode("panel")
            }}
          >

            {/* MAIN MENU */}
            {mode === "main" && (
              <>
                {/* {hasPanels && (
                  <SidebarMenuItem>
                    <SidebarMenuButton className="text-muted-foreground" onClick={() => setMode("panel")}>
                      <ChevronRight/> Page
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )} */}

                {mainNav.map(item => (
                  <div key={item.href}>
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        onClick={() => router.push(item.href)}
                        isActive={pathname === item.href}
                        className="group/button flex flex-row justify-between items-center"
                      >
                        {item.label}
                        {pathname !== item.href && <ChevronRight className="text-muted-foreground opacity-0 group-hover/button:opacity-100" />}
                        {pathname === item.href && <div className="bg-muted-foreground min-h-1.5 min-w-1.5 mr-1.25 rounded-full" />}
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    {/* {item.label == "Pricing" && (
                      <div className="w-full border-b mt-2 mb-1 p-0" />
                    )} */}
                  </div>
                ))}
              </>
            )}

            {/* PANEL MENU */}
            {mode === "panel" && (
              <>
                <SidebarMenuItem>
                  <SidebarMenuButton className="text-muted-foreground hover:text-muted-foreground active:text-muted-foreground" onClick={() => setMode("main")}>
                    <ChevronLeft />Back
                  </SidebarMenuButton>
                </SidebarMenuItem>

                {pageMenu.map(item => (
                  <SidebarMenuItem key={item.panelId}>
                    <SidebarMenuButton
                      isActive={activePanel === item.panelId}
                      onClick={() =>
                        router.replace(`${pathname}?panel=${item.panelId}`)
                      }
                      className="group/button flex flex-row justify-between items-center"
                    >
                      {item.label}
                      {activePanel !== item.panelId && <ChevronRight className="text-muted-foreground opacity-0 group-hover/button:opacity-100" />}
                      {activePanel === item.panelId && <div className="bg-muted-foreground min-h-1.5 min-w-1.5 mr-1.25 rounded-full" />}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </>
            )}

          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  )
}
